import React, { useState, useEffect } from 'react'
import { X, Calendar, User, Award, Clock, AlertCircle, Save, History, ChevronDown, ChevronUp } from 'lucide-react'
import { apiClient } from '@/utils/api'
import { getTodayDateString, parseDueDate } from '@/utils/timezoneUtils'
import toast from 'react-hot-toast'

interface Assignment {
  id: string
  chore_id: string
  assigned_to: string | null
  assigned_to_name: string
  status: string
  points_offered: number
  is_bonus?: boolean
  is_high_priority?: boolean
  due_date: string
  notes?: string
  priority?: string
  reminder_sent?: boolean
  open_mode?: string | null
  chore: {
    id: string
    name: string
    description: string
    base_points: number
    category: string
    estimated_minutes: number
    difficulty: string
  }
}

interface Child {
  id: string
  username: string
  display_name: string
}

interface EditModalProps {
  assignment: Assignment
  children: Child[]
  isOpen: boolean
  onClose: () => void
  onSave: () => void
}

export default function ChoreAssignmentEditModal({ 
  assignment, 
  children, 
  isOpen, 
  onClose, 
  onSave 
}: EditModalProps) {
  const isOpenAssignment = assignment.status === 'open' || !assignment.assigned_to || assignment.assigned_to === 'null'
  const [editData, setEditData] = useState({
    assigned_to: isOpenAssignment ? '' : (assignment.assigned_to || ''),
    due_date: assignment.due_date,
    due_time: '',
    points_offered: assignment.points_offered,
    is_bonus: assignment.is_bonus || false,
    is_high_priority: assignment.is_high_priority || false,
    status: assignment.status,
    notes: assignment.notes || '',
    priority: assignment.priority || '3-5',
    open_mode: assignment.open_mode || ''
  })
  
  const [saving, setSaving] = useState(false)
  const [deleteConfirm, setDeleteConfirm] = useState(false)
  const [pointChangeConfirm, setPointChangeConfirm] = useState<{
    show: boolean
    message: string
    action: 'save' | 'delete'
    details: string
  } | null>(null)
  const [auditLogs, setAuditLogs] = useState<any[]>([])
  const [showHistory, setShowHistory] = useState(false)
  const [loadingHistory, setLoadingHistory] = useState(false)

  useEffect(() => {
    if (assignment) {
      // Parse the ISO date string properly with timezone handling
      const dueDate = parseDueDate(assignment.due_date)
      // Format as YYYY-MM-DD in local timezone
      const formattedDueDate = dueDate.toLocaleDateString('en-CA') // en-CA gives YYYY-MM-DD format
      // Extract time in local timezone (HH:MM format)
      const hours = dueDate.getHours().toString().padStart(2, '0')
      const minutes = dueDate.getMinutes().toString().padStart(2, '0')
      const formattedDueTime = `${hours}:${minutes}`

      setEditData({
        assigned_to: (assignment.status === 'open' || !assignment.assigned_to || assignment.assigned_to === 'null') ? '' : assignment.assigned_to,
        due_date: formattedDueDate,
        due_time: formattedDueTime,
        points_offered: assignment.points_offered,
        is_bonus: assignment.is_bonus || false,
        is_high_priority: assignment.is_high_priority || false,
        status: assignment.status,
        notes: assignment.notes || '',
        priority: assignment.priority || '3-5',
            open_mode: assignment.open_mode || ''
      })
    }
  }, [assignment])

  // Fetch audit logs when modal opens
  useEffect(() => {
    const fetchAuditLogs = async () => {
      if (isOpen && assignment?.id) {
        setLoadingHistory(true)
        try {
          const response = await apiClient.get(`/api/assignments/${assignment.id}/audit-log`)
          setAuditLogs(Array.isArray(response) ? response : [])
        } catch (error) {
          console.error('Failed to fetch audit logs:', error)
          setAuditLogs([])
        } finally {
          setLoadingHistory(false)
        }
      }
    }
    fetchAuditLogs()
  }, [isOpen, assignment?.id])

  const checkPointChanges = () => {
    // Check if this will affect points
    if (assignment.status === 'verified') {
      const changes: string[] = []
      let pointImpact = ''
      
      // Check if assigned person is changing
      if (editData.assigned_to !== assignment.assigned_to) {
        const oldChild = children.find(c => c.id === assignment.assigned_to)?.display_name || 'Unassigned'
        const newChild = children.find(c => c.id === editData.assigned_to)?.display_name || 'Unassigned'
        changes.push(`Reassigning from ${oldChild} to ${newChild}`)
        if (assignment.assigned_to) {
          pointImpact += `• ${assignment.points_offered} points will be removed from ${oldChild}\n`
        }
        if (editData.assigned_to) {
          pointImpact += `• ${assignment.points_offered} points will be added to ${newChild}\n`
        }
      }
      
      // Check if status is changing from verified
      if (editData.status !== 'verified') {
        changes.push(`Changing status from verified to ${editData.status}`)
        const child = children.find(c => c.id === assignment.assigned_to)?.display_name || 'Unknown'
        pointImpact += `• ${assignment.points_offered} points will be removed from ${child}\n`
      }
      
      // Check if points are changing
      if (editData.points_offered !== assignment.points_offered && editData.status === 'verified') {
        const diff = editData.points_offered - assignment.points_offered
        const child = children.find(c => c.id === editData.assigned_to || assignment.assigned_to)?.display_name || 'Unknown'
        if (diff > 0) {
          pointImpact += `• ${diff} points will be added to ${child}\n`
        } else {
          pointImpact += `• ${Math.abs(diff)} points will be removed from ${child}\n`
        }
        changes.push(`Changing points from ${assignment.points_offered} to ${editData.points_offered}`)
      }
      
      if (changes.length > 0) {
        return {
          hasChanges: true,
          message: `This assignment has already been verified. ${changes.join(', ')}.`,
          details: pointImpact
        }
      }
    }
    return { hasChanges: false }
  }
  
  const handleSave = async (skipConfirmation = false) => {
    // Check for point changes first (unless skipping confirmation)
    if (!skipConfirmation) {
      const pointCheck = checkPointChanges()
      if (pointCheck.hasChanges) {
        setPointChangeConfirm({
          show: true,
          message: pointCheck.message!,
          action: 'save',
          details: pointCheck.details!
        })
        return
      }
    }
    
    setSaving(true)
    setPointChangeConfirm(null)
    try {
      // Combine date and time for due_date
      let due_date = editData.due_date
      if (editData.due_time) {
        due_date = `${editData.due_date}T${editData.due_time}:00`
      }

      // Update assignment
      await apiClient.put(`/api/assignments/${assignment.id}`, {
        assigned_to: editData.assigned_to || null,
        due_date: due_date,
        points_offered: editData.points_offered,
        is_bonus: editData.is_bonus,
        is_high_priority: editData.is_high_priority || editData.priority === '5-7' || editData.priority === '7-10',
        priority: editData.priority,
        status: editData.status,
        notes: editData.notes,
        open_mode: editData.assigned_to ? null : (editData.open_mode || null)
      })
      
      toast.success('Assignment updated successfully!')
      onSave()
      onClose()
    } catch (error) {
      console.error('Failed to update assignment:', error)
      toast.error('Failed to update assignment')
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (skipConfirmation = false) => {
    // Check if this is a verified assignment (unless skipping confirmation)
    if (!skipConfirmation && assignment.status === 'verified') {
      const child = children.find(c => c.id === assignment.assigned_to)?.display_name || 'Unknown'
      setPointChangeConfirm({
        show: true,
        message: `This assignment has been verified and ${assignment.points_offered} points have been awarded.`,
        action: 'delete',
        details: `• ${assignment.points_offered} points will be removed from ${child}`
      })
      return
    }
    
    if (!deleteConfirm && !skipConfirmation) {
      setDeleteConfirm(true)
      return
    }
    
    try {
      await apiClient.delete(`/api/assignments/${assignment.id}`)
      setPointChangeConfirm(null)
      toast.success('Assignment deleted')
      onSave()
      onClose()
    } catch (error) {
      toast.error('Failed to delete assignment')
    }
  }

  const handleDuplicate = async () => {
    try {
      await apiClient.post('/api/assignments', {
        chore_id: assignment.chore_id,
        assigned_to: editData.assigned_to || null,
        due_date: editData.due_date,
        points_offered: editData.points_offered,
        notes: editData.notes,
        priority: editData.priority
      })
      toast.success('Assignment duplicated!')
      onSave()
    } catch (error) {
      toast.error('Failed to duplicate assignment')
    }
  }

  const isOpenChore = assignment.status === 'open' || !assignment.assigned_to || assignment.assigned_to === 'null'

  const getStatusOptions = () => {
    // If open/unassigned, show open option
    if (!editData.assigned_to || editData.status === 'open') {
      return [
        { value: 'open', label: 'Open (Available for bidding/claiming)' },
        { value: 'pending', label: 'Pending (Not started)' }
      ]
    }
    // If assigned, show all work states
    return [
      { value: 'pending', label: 'Pending (Not started)' },
      { value: 'in_progress', label: 'In progress (Working on it)' },
      { value: 'completed', label: 'Completed (Awaiting verification)' },
      { value: 'verified', label: 'Verified (Confirmed by parent)' }
    ]
  }

  const formatAuditLogEntry = (log: any) => {
    const changes: string[] = []

    if (log.old_status && log.new_status) {
      changes.push(`Status: ${log.old_status} → ${log.new_status}`)
    }
    if (log.old_assigned_to || log.new_assigned_to) {
      const oldUser = log.old_assigned_to?.display_name || 'Unassigned'
      const newUser = log.new_assigned_to?.display_name || 'Unassigned'
      changes.push(`Assigned: ${oldUser} → ${newUser}`)
    }
    if (log.old_points_offered !== null && log.new_points_offered !== null) {
      changes.push(`Points: ${log.old_points_offered} → ${log.new_points_offered}`)
    }
    if (log.old_due_date || log.new_due_date) {
      const oldDate = log.old_due_date ? new Date(log.old_due_date.includes?.('T') ? log.old_due_date : log.old_due_date + 'T12:00:00').toLocaleDateString() : 'None'
      const newDate = log.new_due_date ? new Date(log.new_due_date.includes?.('T') ? log.new_due_date : log.new_due_date + 'T12:00:00').toLocaleDateString() : 'None'
      changes.push(`Due Date: ${oldDate} → ${newDate}`)
    }

    if (changes.length === 0) {
      return log.change_type.replace('_', ' ')
    }

    return changes.join(', ')
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold">Edit Assignment</h2>
            <p className="text-sm text-gray-500 mt-1">{assignment.chore.name}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Chore Details (Read-only) */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="font-medium mb-2">Chore Details</h3>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-500">Category:</span>
                <span className="ml-2 font-medium">{assignment.chore.category}</span>
              </div>
              <div>
                <span className="text-gray-500">Difficulty:</span>
                <span className="ml-2 font-medium">{assignment.chore.difficulty}</span>
              </div>
              <div>
                <span className="text-gray-500">Base Points:</span>
                <span className="ml-2 font-medium">{assignment.chore.base_points}</span>
              </div>
              <div>
                <span className="text-gray-500">Est. Time:</span>
                <span className="ml-2 font-medium">{assignment.chore.estimated_minutes} min</span>
              </div>
            </div>
            {assignment.chore.description && (
              <p className="text-sm text-gray-600 mt-2">{assignment.chore.description}</p>
            )}
          </div>

          {/* Assignment Details */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Assigned To */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <User className="w-4 h-4 inline mr-1" />
                Assigned To
              </label>
              <select
                value={editData.assigned_to}
                onChange={(e) => {
                  setEditData({ ...editData, assigned_to: e.target.value })
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Open Assignment (Anyone)</option>
                {children.map(child => (
                  <option key={child.id} value={child.id}>
                    {child.display_name}
                  </option>
                ))}
              </select>
            </div>

            {/* Open Mode - show when unassigned */}
            {!editData.assigned_to && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Open Mode
                </label>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setEditData({...editData, open_mode: 'claim'})}
                    className={`px-3 py-2 rounded-lg text-sm font-medium border ${editData.open_mode === 'claim' ? 'bg-green-600 text-white border-green-600' : 'bg-white text-gray-600 border-gray-300'}`}
                  >
                    First to Claim
                  </button>
                  <button
                    type="button"
                    onClick={() => setEditData({...editData, open_mode: 'bid'})}
                    className={`px-3 py-2 rounded-lg text-sm font-medium border ${editData.open_mode === 'bid' ? 'bg-amber-500 text-white border-amber-500' : 'bg-white text-gray-600 border-gray-300'}`}
                  >
                    Bidding
                  </button>
                </div>
              </div>
            )}

            {/* Due Date */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <Calendar className="w-4 h-4 inline mr-1" />
                Due Date
              </label>
              <input
                type="date"
                value={editData.due_date}
                onChange={(e) => setEditData({...editData, due_date: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* Due Time */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <Clock className="w-4 h-4 inline mr-1" />
                Due Time (optional)
              </label>
              <input
                type="time"
                value={editData.due_time}
                onChange={(e) => setEditData({...editData, due_time: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
              <p className="text-xs text-gray-500 mt-1">Leave empty for end of day</p>
            </div>

            {/* Points */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <Award className="w-4 h-4 inline mr-1" />
                Points Offered
              </label>
              <input
                type="number"
                value={editData.points_offered}
                onChange={(e) => setEditData({...editData, points_offered: parseInt(e.target.value) || 0})}
                min="0"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
              <p className="text-xs text-gray-500 mt-1">
                Base: {assignment.chore.base_points} points
                {editData.is_bonus && (
                  <span className="text-green-600 ml-2">
                    (+50% bonus applied)
                  </span>
                )}
              </p>
            </div>


            {/* Status */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <Clock className="w-4 h-4 inline mr-1" />
                Status
              </label>
              <select
                value={editData.status}
                onChange={(e) => setEditData({...editData, status: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                {getStatusOptions().map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Priority */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <AlertCircle className="w-4 h-4 inline mr-1" />
                Priority
              </label>
              <select
                value={editData.priority}
                onChange={(e) => setEditData({...editData, priority: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="1-3">1-3 (Low Priority)</option>
                <option value="3-5">3-5 (Normal Priority)</option>
                <option value="5-7">5-7 (High Priority)</option>
                <option value="7-10">7-10 (Urgent)</option>
              </select>
            </div>


          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Notes / Special Instructions
            </label>
            <textarea
              value={editData.notes}
              onChange={(e) => setEditData({...editData, notes: e.target.value})}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Add any special instructions or notes..."
            />
          </div>

          {/* Quick Actions */}
          <div className="border-t pt-4">
            <h4 className="text-sm font-medium text-gray-700 mb-2">Quick Actions</h4>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={handleDuplicate}
                className="px-3 py-1 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 text-sm"
              >
                Duplicate Assignment
              </button>
              <button
                type="button"
                onClick={() => {
                  const tomorrow = new Date()
                  tomorrow.setDate(tomorrow.getDate() + 1)
                  // Use toLocaleDateString('en-CA') for YYYY-MM-DD in local timezone (not UTC)
                  setEditData({...editData, due_date: tomorrow.toLocaleDateString('en-CA')})
                }}
                className="px-3 py-1 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 text-sm"
              >
                Set Due Tomorrow
              </button>
              <button
                type="button"
                onClick={() => {
                  const nextWeek = new Date()
                  nextWeek.setDate(nextWeek.getDate() + 7)
                  // Use toLocaleDateString('en-CA') for YYYY-MM-DD in local timezone (not UTC)
                  setEditData({...editData, due_date: nextWeek.toLocaleDateString('en-CA')})
                }}
                className="px-3 py-1 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 text-sm"
              >
                Push to Next Week
              </button>
              <button
                type="button"
                onClick={() => {
                  const basePoints = assignment.chore.base_points
                  const isBonus = !editData.is_bonus
                  const newPoints = isBonus ? Math.round(basePoints * 1.5) : basePoints
                  setEditData({
                    ...editData, 
                    is_bonus: isBonus, 
                    points_offered: newPoints,
                    priority: isBonus ? '5-7' : '3-5' // Set higher priority when bonus is applied
                  })
                }}
                className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                  editData.is_bonus 
                    ? 'bg-yellow-500 text-white hover:bg-yellow-600' 
                    : 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200'
                }`}
              >
                {editData.is_bonus ? '✓ ' : ''}+50% Bonus Points
              </button>
            </div>
          </div>

          {/* Change History Section */}
          <div className="border-t pt-4">
            <button
              onClick={() => setShowHistory(!showHistory)}
              className="flex items-center justify-between w-full px-4 py-2 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <div className="flex items-center">
                <History className="w-4 h-4 mr-2 text-gray-600" />
                <span className="font-medium text-gray-700">Change History</span>
                {loadingHistory && <span className="ml-2 text-sm text-gray-500">(Loading...)</span>}
                {!loadingHistory && auditLogs.length > 0 && (
                  <span className="ml-2 text-sm text-gray-500">({auditLogs.length} {auditLogs.length === 1 ? 'change' : 'changes'})</span>
                )}
              </div>
              {showHistory ? <ChevronUp className="w-4 h-4 text-gray-600" /> : <ChevronDown className="w-4 h-4 text-gray-600" />}
            </button>

            {showHistory && (
              <div className="mt-3 space-y-2 max-h-64 overflow-y-auto">
                {auditLogs.length === 0 && !loadingHistory && (
                  <p className="text-sm text-gray-500 text-center py-4">No change history available</p>
                )}
                {auditLogs.map((log) => (
                  <div key={log.id} className="bg-gray-50 rounded-lg p-3 text-sm">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <p className="font-medium text-gray-900">{formatAuditLogEntry(log)}</p>
                        {log.notes && (
                          <p className="text-gray-600 mt-1 text-xs">{log.notes}</p>
                        )}
                      </div>
                      <span className="text-xs text-gray-500 ml-3 whitespace-nowrap">
                        {new Date(log.created_at).toLocaleString()}
                      </span>
                    </div>
                    {log.changed_by && (
                      <p className="text-xs text-gray-500 mt-1">
                        by {log.changed_by.display_name}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-gray-50 border-t px-6 py-4 flex items-center justify-between">
          <button
            onClick={() => handleDelete()}
            className={`px-4 py-2 rounded-lg transition-colors ${
              deleteConfirm 
                ? 'bg-red-600 text-white hover:bg-red-700' 
                : 'bg-red-100 text-red-700 hover:bg-red-200'
            }`}
          >
            {deleteConfirm ? 'Click Again to Confirm Delete' : 'Delete Assignment'}
          </button>
          
          <div className="flex space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={() => handleSave()}
              disabled={saving}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center"
            >
              {saving ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Save Changes
                </>
              )}
            </button>
          </div>
        </div>
      </div>
      
      {/* Point Change Confirmation Dialog */}
      {pointChangeConfirm?.show && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-start mb-4">
              <AlertCircle className="w-6 h-6 text-yellow-500 mr-3 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Points Will Be Affected</h3>
                <p className="text-gray-600 text-sm">{pointChangeConfirm.message}</p>
              </div>
            </div>
            
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
              <p className="font-medium text-sm text-gray-700 mb-2">Point Changes:</p>
              <div className="text-sm text-gray-600 whitespace-pre-line">
                {pointChangeConfirm.details}
              </div>
            </div>
            
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setPointChangeConfirm(null)}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  setPointChangeConfirm(null)
                  if (pointChangeConfirm.action === 'save') {
                    handleSave(true)  // Skip confirmation this time
                  } else {
                    handleDelete(true)  // Skip confirmation this time
                  }
                }}
                className="px-4 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600"
              >
                Continue with {pointChangeConfirm.action === 'save' ? 'Changes' : 'Deletion'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}